import * as React from 'react'
import { PlantHalt } from '../../../../../types/plantHalts'
import duration from '../../../../../functions/duration'
import { isLineDisabledHalt, lineRowClassName, RIC_SG_SHIFTS, Shift, toRicSgLines, ZoneOption } from './config'
import { sumSeconds } from './durations'

interface Props {
  halts: PlantHalt[]
  zones: ZoneOption[]
  /** Turno selezionato nel filtro, se ce n'è uno. */
  selectedShift?: string
  t: (key: string) => string
}

interface LineTotal {
  key: string
  label: string
  className: string
  seconds: number
  count: number
  /** Tempo in cui la linea è risultata disabilitata, tenuto fuori da `seconds`. */
  disabledSeconds: number
  /** Vero quando la disabilitazione copre tutto il turno selezionato. */
  fullyDisabled: boolean
}

/**
 * Tolleranza sulla copertura del turno: una disabilitazione che comincia qualche secondo
 * dopo l'inizio del turno vale comunque come turno intero.
 */
const FULLY_DISABLED_TOLERANCE_SECONDS = 60

/** Durata del turno in secondi; regge anche il turno che scavalca la mezzanotte. */
const shiftLengthSeconds = (shift: Shift): number => (((shift.endHour - shift.startHour + 24) % 24) || 24) * 3600

/**
 * Totali dei tempi di fermo suddivisi per linea, più il totale complessivo.
 *
 * I totali sono calcolati sull'intero insieme filtrato: la paginazione della tabella è
 * client-side e l'API restituisce tutti i fermi del periodo, quindi `halts` non è la sola
 * pagina visibile.
 *
 * Il tempo di linea disabilitata sta FUORI dal totale dei fermi ed è indicato a parte: una
 * linea spenta per un turno intero non è una linea con otto ore di guasti, e sommarla agli
 * altri fermi renderebbe i totali incomparabili fra linee attive e linee ferme.
 */
const LineTotals = (props: Props) => {
  const { halts, zones, selectedShift, t } = props

  const shift: Shift | undefined = selectedShift
    ? RIC_SG_SHIFTS.filter(candidate => candidate.value === selectedShift)[0]
    : undefined

  const isFullyDisabled = (disabledSeconds: number): boolean =>
    !!shift && disabledSeconds >= shiftLengthSeconds(shift) - FULLY_DISABLED_TOLERANCE_SECONDS

  const totals: LineTotal[] = toRicSgLines(zones).map(zone => {
    const zoneHalts = halts.filter(halt => halt.zoneId === zone.value)
    const disabledSeconds = sumSeconds(zoneHalts.filter(isLineDisabledHalt))
    const realHalts = zoneHalts.filter(halt => !isLineDisabledHalt(halt))
    return {
      key: zone.value,
      label: zone.label,
      className: lineRowClassName(zone.line),
      seconds: sumSeconds(realHalts),
      count: realHalts.length,
      disabledSeconds,
      fullyDisabled: isFullyDisabled(disabledSeconds),
    }
  })

  const knownZoneIds = zones.map(zone => zone.value)
  const orphanHalts = halts.filter(halt => !halt.zoneId || knownZoneIds.indexOf(halt.zoneId) === -1)
  if (orphanHalts.length > 0) {
    const realOrphans = orphanHalts.filter(halt => !isLineDisabledHalt(halt))
    totals.push({
      key: '__no_line__',
      label: t('plantHalts.list.noLine'),
      className: '',
      seconds: sumSeconds(realOrphans),
      count: realOrphans.length,
      disabledSeconds: sumSeconds(orphanHalts.filter(isLineDisabledHalt)),
      fullyDisabled: false,
    })
  }

  const grandTotalSeconds = totals.reduce((acc, line) => acc + line.seconds, 0)
  const grandTotalCount = totals.reduce((acc, line) => acc + line.count, 0)

  return (
    <div className="plant-halts-line-totals mb-3">
      <div className="font-weight-bold text-muted mb-1">{t('plantHalts.list.lineTotals')}</div>
      {totals.map(line => (
        <div
          key={line.key}
          className={`line-total ${line.className} ${line.fullyDisabled ? 'line-total--disabled' : ''}`}
        >
          <span className="font-weight-bold mr-2">{`${line.label} [${line.count ? line.count : '-'}]:`}</span>
          <span>{duration(line.seconds)}</span>
          {line.fullyDisabled ? (
            <span className="ml-2 font-italic">{t('plantHalts.list.lineFullyDisabled')}</span>
          ) : line.disabledSeconds > 0 ? (
            <span className="ml-2 text-muted">
              {`· ${t('plantHalts.list.lineDisabled')} ${duration(line.disabledSeconds)}`}
            </span>
          ) : null}
        </div>
      ))}
      <div className="line-total line-total-all">
        <span className="font-weight-bold mr-2">
          {`${t('plantHalts.list.allLinesTotal')} [${grandTotalCount ? grandTotalCount : '-'}]:`}
        </span>
        <span>{duration(grandTotalSeconds)}</span>
      </div>
    </div>
  )
}

export default LineTotals
